import React, { useState, useCallback } from 'react';
import {
  Plus,
  Trash2,
  Pencil,
  Check,
  X,
  ChevronDown,
  ChevronRight,
  ArrowRight,
  Minus,
  Diamond,
  Triangle,
  CircleDot,
  GitBranch,
} from 'lucide-react';
import { useLLDPractice, Relationship } from '../../context/LLDPracticeContext';
import { canAddRelationship } from '../../utils/lldValidation';

// ─── Types ──────────────────────────────────────────────────────────────────────

type RelationshipType = 'association' | 'aggregation' | 'composition' | 'inheritance' | 'dependency';
type Cardinality = '1-to-1' | '1-to-many' | 'many-to-one' | 'many-to-many';

interface ExpectedRelationship {
  source: string;
  target: string;
  type: string;
  cardinality: string;
}

interface RelationshipDesignerProps {
  expectedRelationships?: ExpectedRelationship[];
}

// ─── Constants ──────────────────────────────────────────────────────────────────

const RELATIONSHIP_TYPES: { value: RelationshipType; label: string; description: string }[] = [
  { value: 'association', label: 'Association', description: 'Solid line — uses or knows about' },
  { value: 'aggregation', label: 'Aggregation', description: 'Open diamond — has-a (weak ownership)' },
  { value: 'composition', label: 'Composition', description: 'Filled diamond — owns (strong ownership)' },
  { value: 'inheritance', label: 'Inheritance', description: 'Triangle arrow — is-a' },
  { value: 'dependency', label: 'Dependency', description: 'Dashed line — depends on' },
];

const CARDINALITY_OPTIONS: { value: Cardinality; label: string }[] = [
  { value: '1-to-1', label: '1 : 1' },
  { value: '1-to-many', label: '1 : Many' },
  { value: 'many-to-one', label: 'Many : 1' },
  { value: 'many-to-many', label: 'Many : Many' },
];

// ─── Helper: Relationship Type Icon ─────────────────────────────────────────────

function RelationshipTypeIcon({ type, className = 'w-4 h-4' }: { type: RelationshipType; className?: string }) {
  switch (type) {
    case 'association':
      return <Minus className={className} />;
    case 'aggregation':
      return <Diamond className={className} />;
    case 'composition':
      return <Diamond className={`${className} fill-current`} />;
    case 'inheritance':
      return <Triangle className={className} />;
    case 'dependency':
      return <CircleDot className={className} />;
    default:
      return <Minus className={className} />;
  }
}

// ─── Component ──────────────────────────────────────────────────────────────────

const RelationshipDesigner: React.FC<RelationshipDesignerProps> = ({
  expectedRelationships = [],
}) => {
  const { state, dispatch } = useLLDPractice();
  const { entities, relationships } = state.workspaceState;
  const { mode } = state.sessionState;

  // Form state
  const [sourceId, setSourceId] = useState('');
  const [targetId, setTargetId] = useState('');
  const [relType, setRelType] = useState<RelationshipType>('association');
  const [cardinality, setCardinality] = useState<Cardinality>('1-to-1');
  const [label, setLabel] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editType, setEditType] = useState<RelationshipType>('association');
  const [editCardinality, setEditCardinality] = useState<Cardinality>('1-to-1');
  const [editLabel, setEditLabel] = useState('');

  // Learn mode reference section
  const [showExpected, setShowExpected] = useState(false);

  const isLearnMode = mode === 'learn';

  // ─── Helpers ────────────────────────────────────────────────────────────────

  const getEntityName = useCallback(
    (entityId: string): string => {
      const entity = entities.find((e) => e.id === entityId);
      return entity?.name || 'Unknown';
    },
    [entities]
  );

  // ─── Add Relationship ──────────────────────────────────────────────────────

  const handleAdd = useCallback(() => {
    if (!sourceId || !targetId) {
      setError('Please select both source and target entities');
      return;
    }

    if (sourceId === targetId) {
      setError('Source and target must be different entities');
      return;
    }

    const validationResult = canAddRelationship(sourceId, targetId, relationships);
    if (!validationResult.valid) {
      setError(validationResult.error || 'Cannot add duplicate relationship');
      return;
    }

    const newRelationship: Relationship = {
      id: crypto.randomUUID(),
      sourceEntityId: sourceId,
      targetEntityId: targetId,
      type: relType,
      cardinality,
      label: label.trim() || undefined,
    };

    dispatch({ type: 'ADD_RELATIONSHIP', payload: newRelationship });

    // Reset form
    setSourceId('');
    setTargetId('');
    setRelType('association');
    setCardinality('1-to-1');
    setLabel('');
    setError(null);
  }, [sourceId, targetId, relType, cardinality, label, relationships, dispatch]);

  // ─── Remove Relationship ───────────────────────────────────────────────────

  const handleRemove = useCallback(
    (id: string) => {
      dispatch({ type: 'REMOVE_RELATIONSHIP', payload: id });
    },
    [dispatch]
  );

  // ─── Edit Relationship ─────────────────────────────────────────────────────

  const handleStartEdit = useCallback((rel: Relationship) => {
    setEditingId(rel.id);
    setEditType(rel.type);
    setEditCardinality(rel.cardinality);
    setEditLabel(rel.label || '');
  }, []);

  const handleSaveEdit = useCallback(() => {
    if (!editingId) return;

    // Remove the old and add updated (since context only supports ADD/REMOVE)
    const existing = relationships.find((r) => r.id === editingId);
    if (!existing) return;

    dispatch({ type: 'REMOVE_RELATIONSHIP', payload: editingId });
    dispatch({
      type: 'ADD_RELATIONSHIP',
      payload: {
        ...existing,
        type: editType,
        cardinality: editCardinality,
        label: editLabel.trim() || undefined,
      },
    });

    setEditingId(null);
    setEditType('association');
    setEditCardinality('1-to-1');
    setEditLabel('');
  }, [editingId, editType, editCardinality, editLabel, relationships, dispatch]);

  const handleCancelEdit = useCallback(() => {
    setEditingId(null);
    setEditType('association');
    setEditCardinality('1-to-1');
    setEditLabel('');
  }, []);

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full gap-4">
      {/* Add Relationship Form */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
        {entities.length < 2 ? (
          <p className="text-sm text-gray-600 dark:text-gray-400 text-center py-2">
            Add at least 2 entities in Entity Discovery to create relationships.
          </p>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <label className="block text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-500 mb-1">Source</label>
                <select
                  value={sourceId}
                  onChange={(e) => { setSourceId(e.target.value); if (error) setError(null); }}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-400"
                >
                  <option value="">Select source…</option>
                  {entities.map((entity) => (<option key={entity.id} value={entity.id}>{entity.name}</option>))}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-500 mb-1">Target</label>
                <select
                  value={targetId}
                  onChange={(e) => { setTargetId(e.target.value); if (error) setError(null); }}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-400"
                >
                  <option value="">Select target…</option>
                  {entities.map((entity) => (<option key={entity.id} value={entity.id}>{entity.name}</option>))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <label className="block text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-500 mb-1">Type</label>
                <select
                  value={relType}
                  onChange={(e) => setRelType(e.target.value as RelationshipType)}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-400"
                >
                  {RELATIONSHIP_TYPES.map((rt) => (<option key={rt.value} value={rt.value}>{rt.label}</option>))}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-500 mb-1">Cardinality</label>
                <select
                  value={cardinality}
                  onChange={(e) => setCardinality(e.target.value as Cardinality)}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-400"
                >
                  {CARDINALITY_OPTIONS.map((c) => (<option key={c.value} value={c.value}>{c.label}</option>))}
                </select>
              </div>
            </div>

            <div className="flex gap-2">
              <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Label (optional)"
                maxLength={50}
                className="flex-1 px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-400"
              />
              <button
                onClick={handleAdd}
                disabled={!sourceId || !targetId}
                className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 transition-colors flex-shrink-0"
              >
                <Plus className="w-4 h-4" /> Add
              </button>
            </div>

            <p className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-500">
              <RelationshipTypeIcon type={relType} className="w-3.5 h-3.5" />
              {RELATIONSHIP_TYPES.find((rt) => rt.value === relType)?.description}
            </p>

            {error && <p className="text-sm text-rose-500 dark:text-rose-400">{error}</p>}
          </div>
        )}
      </div>

      {/* Connections */}
      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
        Connections <span className="text-gray-500 dark:text-gray-500 font-normal">({relationships.length})</span>
      </h3>

      <div className="flex-1 overflow-y-auto -mx-1 px-1 space-y-2">
        {relationships.length === 0 ? (
          <div className="min-h-[140px] flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 dark:border-gray-700 text-center px-6">
            <GitBranch className="w-8 h-8 text-gray-300 dark:text-gray-600 mb-2" />
            <p className="text-sm text-gray-600 dark:text-gray-400">No relationships yet.</p>
            <p className="text-xs text-gray-500 dark:text-gray-500 mt-0.5">Connect two entities above to define how they relate.</p>
          </div>
        ) : (
          relationships.map((rel) =>
            editingId === rel.id ? (
              // Edit Mode
              <div key={rel.id} className="p-3 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-xl space-y-2">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {getEntityName(rel.sourceEntityId)} → {getEntityName(rel.targetEntityId)}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <select
                    value={editType}
                    onChange={(e) => setEditType(e.target.value as RelationshipType)}
                    className="w-full px-2 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/40"
                  >
                    {RELATIONSHIP_TYPES.map((rt) => (<option key={rt.value} value={rt.value}>{rt.label}</option>))}
                  </select>
                  <select
                    value={editCardinality}
                    onChange={(e) => setEditCardinality(e.target.value as Cardinality)}
                    className="w-full px-2 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/40"
                  >
                    {CARDINALITY_OPTIONS.map((c) => (<option key={c.value} value={c.value}>{c.label}</option>))}
                  </select>
                </div>
                <input
                  type="text"
                  value={editLabel}
                  onChange={(e) => setEditLabel(e.target.value)}
                  placeholder="Label (optional)"
                  maxLength={50}
                  className="w-full px-2 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/40"
                />
                <div className="flex gap-2">
                  <button onClick={handleSaveEdit} className="px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm flex items-center gap-1 transition-colors">
                    <Check className="w-3.5 h-3.5" /> Save
                  </button>
                  <button onClick={handleCancelEdit} className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm flex items-center gap-1 transition-colors">
                    <X className="w-3.5 h-3.5" /> Cancel
                  </button>
                </div>
              </div>
            ) : (
              // Display Mode
              <div key={rel.id} className="group flex items-center justify-between p-3.5 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl hover:border-primary-300 dark:hover:border-primary-700 transition-colors">
                <div className="flex items-center gap-2.5 flex-1 min-w-0">
                  <span className="w-8 h-8 rounded-lg bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-300 flex items-center justify-center flex-shrink-0">
                    <RelationshipTypeIcon type={rel.type} className="w-4 h-4" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {getEntityName(rel.sourceEntityId)}
                      <ArrowRight className="inline w-3.5 h-3.5 mx-1 text-gray-400" />
                      {getEntityName(rel.targetEntityId)}
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      {RELATIONSHIP_TYPES.find((rt) => rt.value === rel.type)?.label} · {CARDINALITY_OPTIONS.find((c) => c.value === rel.cardinality)?.label}
                      {rel.label && ` · "${rel.label}"`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1 ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => handleStartEdit(rel)} className="p-1.5 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors" title="Edit relationship">
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => handleRemove(rel.id)} className="p-1.5 text-gray-400 hover:text-rose-600 dark:hover:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-lg transition-colors" title="Remove relationship">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )
          )
        )}
      </div>

      {/* Learn Mode: Expected Relationships Reference */}
      {isLearnMode && expectedRelationships.length > 0 && (
        <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-800/40 px-3 py-2.5">
          <button
            onClick={() => setShowExpected(!showExpected)}
            className="flex items-center gap-2 w-full text-left text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            {showExpected ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            Compare with expected
            <span className="ml-auto text-xs text-gray-500 dark:text-gray-500">{expectedRelationships.length}</span>
          </button>
          {showExpected && (
            <div className="mt-2 space-y-1.5">
              {expectedRelationships.map((expected, idx) => (
                <div key={`${expected.source}-${expected.target}-${idx}`} className="flex items-center gap-2 px-2.5 py-1.5 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg text-sm">
                  <RelationshipTypeIcon type={expected.type as RelationshipType} className="w-3.5 h-3.5 text-primary-500 dark:text-primary-400 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300 truncate">
                    {expected.source}
                    <ArrowRight className="inline w-3 h-3 mx-1 text-gray-400" />
                    {expected.target}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-500 ml-auto flex-shrink-0">{expected.type} · {expected.cardinality}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RelationshipDesigner;
